import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Container, Divider, Stack, Typography } from '@mui/material';
import { grey, blueGrey } from '@mui/material/colors';
import * as Yup from 'yup';
import Helmet from 'react-helmet';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { SIGN_UP_MUTATION } from '../../gql';
import {
  MIN_PASSWORD_LENGTH,
  emailRegex,
  useYupValidationResolver,
} from '../../helpers';
import { Logo } from '../../components';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceTextField,
} from '../../components/design-system';

const containerStyles = { height: '100%', display: 'flex' };
const rootStyles = { width: '100%', height: '100%', background: blueGrey[900] };
const stackStyles = {
  boxShadow: `0 8px 24px ${grey[800]}`,
  width: '100%',
  bgcolor: 'background.paper',
  borderRadius: 2,
};
const submitButtonStyles = {
  borderRadius: 2,
  fontSize: '1rem',
};
const privacyLinkStyles = { color: '#D76B90' };

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('E-mail is mandatory field.')
    .matches(emailRegex, 'Please, provide a valid e-mail.'),
  password: Yup.string()
    .required('Password is mandatory field.')
    .min(
      MIN_PASSWORD_LENGTH,
      `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
    ),
  confirmPassword: Yup.string()
    .required('Please, confirm password.')
    .oneOf([Yup.ref('password')], 'Passwords do not match.'),
});

const SignUp = React.memo(() => {
  const [errors, setErrors] = useState<Array<{ error: string }>>([]);
  const [isComplete, setIsComplete] = useState(false);
  const resolver = useYupValidationResolver(validationSchema, setErrors);
  const { register, handleSubmit } = useForm({ resolver });

  const [signUp, { loading }] = useMutation(SIGN_UP_MUTATION);

  const onSubmit = async (values: { email: string; password: string }) => {
    const { data } = await signUp({
      variables: {
        email: values.email,
        password: values.password,
      },
    });

    if (data && !data.signUp.errors) {
      setIsComplete(true);
    } else {
      setErrors(data!.signUp!.errors);
    }
  };

  return (
    <div style={rootStyles}>
      <Helmet title="Sign-up"></Helmet>
      <Container maxWidth="xs" sx={containerStyles}>
        <Stack
          spacing={2}
          alignItems="flex-start"
          justifyContent="center"
          component={isComplete ? 'div' : 'form'}
          onSubmit={isComplete ? undefined : handleSubmit(onSubmit)}
          sx={stackStyles}
          px={4}
          pt={8}
          pb={12}
          mt={18}
          mb="auto"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={2}
            width="100%"
          >
            <Logo size={92} />
            <Typography textAlign="center" fontWeight="bold" variant="h2">
              Acolyte
            </Typography>
          </Stack>
          {isComplete ? (
            <>
              <Stack width="100%" spacing={1}>
                <Typography variant="h6" fontWeight="bold">
                  User registered
                </Typography>
                <Typography>
                  Verification email sent — please check your inbox and click
                  the link in the email. You can close this page in the
                  meantime.
                </Typography>
              </Stack>
              <Divider />
              <Stack width="100%" alignItems="center">
                <Typography variant="caption">
                  <Link to="/" style={{ color: '#D76B90' }}>
                    Home
                  </Link>
                </Typography>
                <Typography variant="caption">
                  <a href="/privacy" style={privacyLinkStyles}>
                    Privacy policy
                  </a>
                </Typography>
              </Stack>
            </>
          ) : (
            <>
              <FormField width="100%">
                <FieldLabel htmlFor="email">E-mail</FieldLabel>
                <SurfaceTextField
                  fullWidth
                  id="email"
                  surface="sunken"
                  type="text"
                  placeholder="e-mail"
                  {...register('email', {
                    setValueAs: (value) => value.toLowerCase(),
                  })}
                />
              </FormField>
              <FormField width="100%">
                <FieldLabel htmlFor="password">Password</FieldLabel>
                <SurfaceTextField
                  fullWidth
                  id="password"
                  surface="sunken"
                  type="password"
                  {...register('password')}
                />
              </FormField>
              <FormField width="100%">
                <FieldLabel htmlFor="confirmPassword">
                  Confirm password
                </FieldLabel>
                <SurfaceTextField
                  fullWidth
                  id="confirmPassword"
                  surface="sunken"
                  type="password"
                  {...register('confirmPassword')}
                />
              </FormField>
              <Divider color="transparent" />
              <Button
                type="submit"
                fullWidth
                sx={submitButtonStyles}
                disabled={loading}
              >
                Sign up
              </Button>
              <Divider />
              <Stack width="100%">
                {errors.length > 0 && (
                  <Stack>
                    {errors.map((item: { error: string }) => (
                      <Typography color="red">{item.error}</Typography>
                    ))}
                  </Stack>
                )}
              </Stack>
              <Stack width="100%" alignItems="center">
                <Stack direction="row" justifyContent="center" width="100%">
                  <Typography variant="caption">
                    Already have an account?{' '}
                    <Link to="/signin" style={{ color: '#FF0070' }}>
                      Sign-in
                    </Link>
                  </Typography>
                </Stack>
                <Typography variant="caption">
                  <Link to="/" style={{ color: '#D76B90' }}>
                    Home
                  </Link>
                </Typography>
                <Typography variant="caption">
                  <a href="/privacy" style={privacyLinkStyles}>
                    Privacy policy
                  </a>
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </Container>
    </div>
  );
});

export { SignUp };
